// src/modules/app-views/crm/pages/Kanban/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useRef, useState } from 'react';
import { Spin } from 'antd';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { useNavigate } from 'react-router-dom';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import useZustand from 'hooks/useZustand';
import { generateMongoObjectId } from 'utils/helpers';
import useUrl from 'hooks/useUrl';
import crmService from '../../service';
import { useUserRole } from '../../useUserRole';
import KanbanColumn from './components/KanbanColumn';
import BackToHomeLink from 'components/core/BackToHomeLink';
import BoardSettingsModal from '../Boards/modals/BoardSettingsModal';
import { boardSettingsRef } from './boardSettingsRef';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
// Stable empty array so board?.columns || EMPTY_COLS never creates a new reference on every render
const EMPTY_COLS = [];

// SessionStorage key prefix for persisting horizontal scroll position
const SCROLL_STORAGE_KEY = 'crm_kanban_scroll_';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CrmKanban = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // Parse boardUid from the URL (routing endpoint: /board/:boardUid)
  const { params } = useUrl();
  // Subscribe to parameters so component re-renders when SSE delivers them after F5
  const storedParameters = useZustand('app', 'parameters');
  const storedUser = useZustand('auth', 'user');
  const setReadyStore = useZustand('module', 'setReady');
  const setPageStore = useZustand('module', 'setPage');
  const navigate = useNavigate();
  const { canManage } = useUserRole();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const wrapperRef = useRef(null);
  // Prevents loadBoardData from firing when a column was added optimistically
  const skipNextColumnReloadRef = useRef(false);
  // Tracks the uid of a newly added column to trigger scroll + auto-rename
  const newColumnUidRef = useRef(null);
  // Prevents the redirect effect from calling setPageStore on every SSE update
  const hasRedirectedRef = useRef(false);
  // Tracks last known columns count so cardOrder changes can be handled without a full reload
  const prevColumnsLengthRef = useRef(0);
  // Auto-scroll during horizontal drag
  const isDraggingRef = useRef(false);
  const mousePosRef = useRef({ x: 0 });
  const autoScrollRafRef = useRef(null);
  const readyFiredRef = useRef(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const boardUid = params?.boardUid;
  const boards = storedParameters?.modules?.crm?.boards || [];

  const board = boards.find(b => b.uid === boardUid) || null;
  // Use EMPTY_COLS (stable reference) so the sync effect does not loop when board is null
  const columns = board?.columns || EMPTY_COLS;
  const fixedColumns = board?.fixedColumns || false;

  // Hash of uid+cardOrder — changes when columns are added/removed OR when cardOrder is updated.
  // Used as the reload trigger key so re-sorting fires automatically after a cardOrder save.
  const columnsReloadKey = columns
    .map(c => `${c.uid}:${c.cardOrder || 'oldest_first'}`)
    .join(',');

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [columnCards, setColumnCards] = useState({});
  const [localColumns, setLocalColumns] = useState([]);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [newColumnUid, setNewColumnUid] = useState(null);
  const [showBoardSettingsModal, setShowBoardSettingsModal] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState(null);

  // Bridge for dropdown menu button to open board settings
  boardSettingsRef.current = () => {
    setSelectedBoard(board);
    setShowBoardSettingsModal(true);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Dismiss SimpleLoadingPage once parameters are available (same pattern as CrmBoards)
  // Run only once — subsequent SSE updates should not re-trigger setReady
  useEffect(() => {
    if (storedParameters?.modules?.crm && !readyFiredRef.current) {
      readyFiredRef.current = true;
      setReadyStore(true);
    }
  }, [storedParameters?.modules?.crm]);

  // Redirect to initial page when parameters are loaded but the board does not exist
  useEffect(() => {
    if (!storedParameters?.modules?.crm || !boardUid) return;
    if (hasRedirectedRef.current) return;
    const boardExists = (storedParameters.modules.crm.boards || []).some(b => b.uid === boardUid);
    if (!boardExists) {
      hasRedirectedRef.current = true;
      setPageStore({ navigate, pageName: 'boards' });
    }
  }, [storedParameters?.modules?.crm, boardUid]);

  // Sync localColumns from SSE parameters — functional setState bails out when content unchanged.
  // Compares full column content (not just uid) so automation/SLA changes are detected.
  useEffect(() => {
    const sorted = [...columns].sort((a, b) => (a.order || 0) - (b.order || 0));
    setLocalColumns(prev => {
      if (prev.length !== sorted.length) return sorted;
      const same = prev.every((c, i) => JSON.stringify(c) === JSON.stringify(sorted[i]));
      return same ? prev : sorted;
    });
  }, [columns]);

  // When SSE delivers the newly added column, scroll to it and trigger auto-rename
  useEffect(() => {
    const uid = newColumnUidRef.current;
    if (!uid) return;
    const found = columns.some(c => c.uid === uid);
    if (!found) return;
    newColumnUidRef.current = null;
    setNewColumnUid(uid);
    requestAnimationFrame(() => {
      wrapperRef.current?.scrollTo({ left: wrapperRef.current.scrollWidth, behavior: 'smooth' });
    });
  }, [columns]);

  useEffect(() => {
    // Wait for both boardUid and columns to be available (columns arrive via SSE after F5)
    if (!boardUid || columns.length === 0) return;

    // Skip reload when a column was added/removed/reordered optimistically
    if (skipNextColumnReloadRef.current) {
      skipNextColumnReloadRef.current = false;
      prevColumnsLengthRef.current = columns.length;
      return;
    }

    if (columns.length !== prevColumnsLengthRef.current) {
      // Column count changed (add/remove) → full reload
      prevColumnsLengthRef.current = columns.length;
      loadBoardData();
      return;
    }

    // Only column content changed (e.g. cardOrder) → re-sort in memory, no API call, no flash
    setColumnCards(prev => {
      const next = { ...prev };
      for (const col of columns) {
        const cards = prev[col.uid];
        if (!cards || cards.length < 2) continue;
        const cardOrder = col.cardOrder || 'oldest_first';
        if (cardOrder === 'free') continue; // user-managed order, leave as-is
        next[col.uid] = [...cards].sort((a, b) =>
          cardOrder === 'newest_first'
            ? new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            : new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
      }
      return next;
    });
  // columnsReloadKey encodes uid+cardOrder — fires on both length and cardOrder changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardUid, columnsReloadKey]);

  // Save horizontal scroll position on every scroll event (persists to sessionStorage).
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || !boardUid) return;
    const handler = () => sessionStorage.setItem(SCROLL_STORAGE_KEY + boardUid, String(el.scrollLeft));
    el.addEventListener('scroll', handler, { passive: true });
    return () => el.removeEventListener('scroll', handler);
  }, [boardUid, loading]); // re-attach after loading=false when wrapper mounts

  // Restore horizontal scroll position when loading finishes (wrapper just mounted).
  useEffect(() => {
    if (loading || !boardUid) return;
    const savedScroll = sessionStorage.getItem(SCROLL_STORAGE_KEY + boardUid);
    if (!savedScroll) return;
    // setTimeout ensures the DOM is fully painted after loading=false re-render
    const id = setTimeout(() => {
      if (wrapperRef.current) wrapperRef.current.scrollLeft = Number(savedScroll);
    }, 50);
    return () => clearTimeout(id);
  }, [loading, boardUid]);

  // Auto-scroll the kanban wrapper when dragging near viewport edges.
  //
  // KEY DESIGN: the wrapper must keep overflow:auto so @hello-pangea/dnd registers
  // it as a scroll container and adds an element-level scroll listener. Scroll events
  // do NOT bubble in the DOM — the library only tracks scrolls on elements it has
  // directly registered. With overflow:auto, every scrollLeft change (ours AND the
  // library's own auto-scroll) fires on that registered element → library updates
  // droppable positions correctly → drop zones stay in sync.
  //
  // pointermove is used because the library suppresses window mousemove during drag.
  useEffect(() => {
    const EDGE_ZONE = 100; // px from viewport edge that triggers scrolling
    const MAX_SPEED = 8;   // px per frame — library also contributes, keep moderate

    const onPointerMove = e => { mousePosRef.current = { x: e.clientX }; };
    document.addEventListener('pointermove', onPointerMove);

    const runScroll = () => {
      if (isDraggingRef.current && wrapperRef.current) {
        const x = mousePosRef.current.x;
        const vw = window.innerWidth;
        let speed = 0;
        if (x < EDGE_ZONE) {
          speed = -((EDGE_ZONE - x) / EDGE_ZONE) * MAX_SPEED;
        } else if (x > vw - EDGE_ZONE) {
          speed = ((x - (vw - EDGE_ZONE)) / EDGE_ZONE) * MAX_SPEED;
        }
        if (speed !== 0) wrapperRef.current.scrollLeft += speed;
      }
      autoScrollRafRef.current = requestAnimationFrame(runScroll);
    };

    autoScrollRafRef.current = requestAnimationFrame(runScroll);

    return () => {
      document.removeEventListener('pointermove', onPointerMove);
      if (autoScrollRafRef.current) cancelAnimationFrame(autoScrollRafRef.current);
    };
  }, []);

  // Detect horizontal scroll overflow for shadow indicators
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || loading) return;

    const checkScroll = () => {
      setCanScrollLeft(el.scrollLeft > 0);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };

    checkScroll();
    el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [loading, columns.length]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const loadBoardData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await crmService.fetchBoardView(boardUid);

      // Initialize empty arrays for each column uid
      const grouped = {};
      for (const col of columns) {
        grouped[col.uid] = [];
      }

      // Group cards by columnUid — sort order depends on the column's cardOrder setting
      for (const group of data || []) {
        const colUid = group.columnUid;
        if (colUid && grouped[colUid] !== undefined) {
          const col = columns.find(c => c.uid === colUid);
          const cardOrder = col?.cardOrder || 'oldest_first';
          const cards = group.cards || [];
          if (cardOrder === 'newest_first') {
            grouped[colUid] = cards.sort(
              (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
            );
          } else if (cardOrder === 'free') {
            // Respect backend columnOrder (manual drag position)
            grouped[colUid] = cards.sort((a, b) => (a.columnOrder ?? 0) - (b.columnOrder ?? 0));
          } else {
            // oldest_first (default)
            grouped[colUid] = cards.sort(
              (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
            );
          }
        }
      }

      setColumnCards(grouped);
    } catch (err) {
      console.error('[CrmKanban] Error loading board:', err);
    } finally {
      setLoading(false);
    }
  }, [boardUid, columns]);

  const handleDragStart = () => { isDraggingRef.current = true; };

  const handleDragEnd = async result => {
    isDraggingRef.current = false;
    const { source, destination, draggableId, type } = result;

    // Dropped outside
    if (!destination) return;

    // Same position
    if (source.droppableId === destination.droppableId && source.index === destination.index)
      return;

    // ── Column reorder ──────────────────────────────────────────────────────────
    if (type === 'COLUMN') {
      // Columns are fixed by board setting — reorder is disabled for guests
      if (!canManage && fixedColumns) return;
      // Protect initial and final columns: cannot be moved or displaced
      if (localColumns[source.index]?.initial || localColumns[source.index]?.final) return;
      if (destination.index === 0 && localColumns[0]?.initial) return;
      const lastIdx = localColumns.length - 1;
      if (destination.index === lastIdx && localColumns[lastIdx]?.final) return;

      const reordered = [...localColumns];
      const [moved] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, moved);
      // Assign sequential order values so the backend stores the new order
      const reorderedWithOrder = reordered.map((col, idx) => ({ ...col, order: idx + 1 }));

      // Optimistic update
      setLocalColumns(reorderedWithOrder);
      // Skip the card-data reload triggered by SSE when columns update
      skipNextColumnReloadRef.current = true;

      try {
        await crmService.reorderColumns(boardUid, reorderedWithOrder);
      } catch (err) {
        console.error('[CrmKanban] Error reordering columns:', err);
        // Revert to last known good order from SSE
        setLocalColumns([...columns].sort((a, b) => (a.order || 0) - (b.order || 0)));
      }
      return;
    }

    // ── Card move ───────────────────────────────────────────────────────────────
    const sourceColUid = source.droppableId;
    const destColUid = destination.droppableId;

    // Guest users cannot move cards between columns unless board allows it
    if (sourceColUid !== destColUid && !canManage && !board?.allowMoveCards) return;

    // Columns with fixed card order (oldest_first / newest_first) block intra-column reorder
    if (sourceColUid === destColUid) {
      const col = localColumns.find(c => c.uid === sourceColUid);
      const cardOrder = col?.cardOrder || 'oldest_first';
      if (cardOrder !== 'free') return;
    }
    const newColumnCards = { ...columnCards };

    // Remove from source
    const sourceCards = [...(newColumnCards[sourceColUid] || [])];
    const [movedCard] = sourceCards.splice(source.index, 1);
    newColumnCards[sourceColUid] = sourceCards;

    // Add to destination
    const destCards = [...(newColumnCards[destColUid] || [])];
    destCards.splice(destination.index, 0, movedCard);

    // If destination column has a fixed order, re-sort immediately so the card
    // snaps into the correct position regardless of where it was dropped.
    const destCol = localColumns.find(c => c.uid === destColUid);
    const destCardOrder = destCol?.cardOrder || 'oldest_first';
    if (destCardOrder !== 'free') {
      destCards.sort((a, b) =>
        destCardOrder === 'newest_first'
          ? new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          : new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
    }

    newColumnCards[destColUid] = destCards;

    // Optimistic update — card moves visually so drag animation completes
    setColumnCards(newColumnCards);

    // ── Validation: revert + modal if card cannot leave source column ────
    const sourceCardsForCheck = columnCards[sourceColUid] || [];
    const cardToMove = sourceCardsForCheck.find(c => c._id === draggableId);

    // 1) Filed (archived) card cannot be moved
    if (cardToMove?.filedAt) {
      setColumnCards(columnCards); // revert
      appService.modal({
        flag: 'warning',
        title: 'card_filed',
        content: 'cannot_move_filed_card',
      });
      return;
    }

    // 1b) Deal closed/lost card cannot be moved
    if (cardToMove?.status === 'contract_closed' || cardToMove?.status === 'lost') {
      setColumnCards(columnCards); // revert
      return;
    }

    // 2) Attendance not started or rescheduled — card cannot be moved
    if (!cardToMove?.attendanceStartedAt || cardToMove?.attendanceRescheduledAt) {
      setColumnCards(columnCards); // revert
      appService.modal({
        flag: 'warning',
        title: 'attendance_not_started',
        content: 'start_attendance_before_moving',
      });
      return;
    }

    // 3) Required form fields not filled in the source column
    const formFields = board?.formFields || [];
    const requiredFields = formFields.filter(f =>
      (f.requiredInColumns || []).includes(sourceColUid)
    );

    if (requiredFields.length > 0) {
      const cardFormData = cardToMove?.formData || {};
      const missingFields = requiredFields.filter(f => {
        const val = cardFormData[f.fieldName];
        return val === undefined || val === null || val === '' ||
               (Array.isArray(val) && val.length === 0);
      });

      if (missingFields.length > 0) {
        setColumnCards(columnCards); // revert
        appService.modal({
          flag: 'warning',
          title: 'required_fields',
          content: translateX('fill_required_fields_before_moving') +
            '\n\n' + missingFields.map(f => '• ' + f.title).join('\n'),
        });
        return;
      }
    }

    // Persist to backend
    const sourceCol = localColumns.find(c => c.uid === sourceColUid);
    const destColumn = localColumns.find(c => c.uid === destColUid);
    try {
      await crmService.moveOpportunity(draggableId, {
        boardUid,
        columnUid: destColUid,
        columnOrder: destination.index,
      });
      // Log movement in follow-up timeline
      if (sourceColUid !== destColUid) {
        crmService.addMovementFollowUp(draggableId, {
          user: storedUser,
          text: translateX('followup_card_moved', {
            from: sourceCol?.title || '?',
            to: destColumn?.title || '?',
          }),
        });
      }
    } catch (err) {
      console.error('[CrmKanban] Error moving card:', err);
      appService.message({ flag: 'e', text: 'error_moving_card' });
      // Revert on error
      loadBoardData();
    }
  };

  const handleAddColumn = useCallback(async () => {
    if (!boardUid) return;
    // Generate uid upfront so optimistic state and backend use the same value
    const uid = generateMongoObjectId();
    // Mark uid so the columns effect can trigger scroll + auto-rename after SSE delivers it
    newColumnUidRef.current = uid;
    // Optimistic update — show column immediately without reloading
    setColumnCards(prev => ({ ...prev, [uid]: [] }));
    // Flag the next columns.length SSE update to skip loadBoardData
    skipNextColumnReloadRef.current = true;
    await crmService.addColumn(boardUid, { title: translateX('new_column'), color: '#8c8c8c', uid });
  }, [boardUid]);

  const handleDeleteColumn = async columnUid => {
    const cards = columnCards[columnUid] || [];
    if (cards.length > 0) {
      appService.message({ flag: 'w', text: 'cannot_remove_column_with_cards' });
      return;
    }

    appService.modal({
      flag: 'confirm',
      title: 'remove_column',
      content: 'delete_column_description',
      okText: 'yes',
      cancelText: 'no',
      okButtonProps: { danger: true },
      onOk: async () => {
        // Optimistic: remove column from local state immediately (no reload flash)
        setColumnCards(prev => {
          const next = { ...prev };
          delete next[columnUid];
          return next;
        });
        // Skip the board reload triggered by SSE when columns.length decreases
        skipNextColumnReloadRef.current = true;
        await crmService.removeColumn(boardUid, columnUid);
      },
    });
  };

  const handleRenameColumn = async (columnUid, title) => {
    await crmService.updateColumn(boardUid, columnUid, { title });
  };

  // Listen for add-column triggered from the header action button
  useEffect(() => {
    window.addEventListener('crm:kanban:add-column', handleAddColumn);
    return () => window.removeEventListener('crm:kanban:add-column', handleAddColumn);
  }, [handleAddColumn]);

  // Listen for manual refresh triggered from the header action button
  useEffect(() => {
    const handler = () => {
      if (boardUid && columns.length > 0) loadBoardData();
    };
    window.addEventListener('crm:kanban:refresh', handler);
    return () => window.removeEventListener('crm:kanban:refresh', handler);
  }, [loadBoardData]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  const shadowStyle = {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 20,
    pointerEvents: 'none',
    zIndex: 1,
    transition: 'opacity 0.3s ease',
  };

  return (
    <>
      <div className="mt-3" style={{ position: 'relative' }}>
        {/* left shadow overlay */}
        <div
          style={{
            ...shadowStyle,
            left: 0,
            background: 'linear-gradient(to right, rgba(0,0,0,0.12), transparent)',
            opacity: canScrollLeft ? 1 : 0,
          }}
        />

        {/* right shadow overlay */}
        <div
          style={{
            ...shadowStyle,
            right: 0,
            background: 'linear-gradient(to left, rgba(0,0,0,0.12), transparent)',
            opacity: canScrollRight ? 1 : 0,
          }}
        />

        {/* overflow:auto keeps @hello-pangea/dnd registered on this element so its
            element-level scroll listener tracks ALL scrollLeft changes (ours + its
            own auto-scroll). Scroll events do not bubble — only element-registered
            listeners fire — so this is the only setup where drop zones stay correct. */}
        <style>{`.kanban-wrapper::-webkit-scrollbar { display: none; }`}</style>
        <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <Droppable droppableId="board-columns" type="COLUMN" direction="horizontal">
            {provided => (
              <div
                ref={el => {
                  provided.innerRef(el);
                  wrapperRef.current = el;
                }}
                {...provided.droppableProps}
                className="kanban-wrapper"
                style={{
                  display: 'flex',
                  gap: 16,
                  alignItems: 'flex-start',
                  overflowX: 'auto',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                }}
              >
                {localColumns.map((column, index) => {
                  const colUid = column.uid;
                  // Admin/owner can always drag; guests are restricted by fixedColumns setting
                  // Initial and final columns: only admin/owner can drag
                  const isFixedColumn = column.initial || column.final || index === 0;
                  const columnDraggable = isFixedColumn ? false : (canManage || !fixedColumns);
                  // Admin/owner always see the column menu; guests respect board.allowColumnEdit
                  const allowColumnEdit = canManage || board?.allowColumnEdit !== false;
                  return (
                    <Draggable
                      key={colUid}
                      draggableId={colUid}
                      index={index}
                      isDragDisabled={!columnDraggable}
                    >
                      {colProvided => (
                        <KanbanColumn
                          column={column}
                          cards={columnCards[colUid] || []}
                          boardUid={boardUid}
                          onDeleteColumn={handleDeleteColumn}
                          onRenameColumn={handleRenameColumn}
                          onRefresh={loadBoardData}
                          autoRename={colUid === newColumnUid}
                          onAutoRenameDone={() => setNewColumnUid(null)}
                          columnDraggable={columnDraggable}
                          canManage={canManage}
                          allowColumnEdit={allowColumnEdit}
                          isFirst={index === 0}
                          innerRef={colProvided.innerRef}
                          draggableProps={colProvided.draggableProps}
                          dragHandleProps={colProvided.dragHandleProps}
                        />
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      <BackToHomeLink style={{ marginTop: 48 }} />

      <BoardSettingsModal
        show={showBoardSettingsModal}
        setShow={setShowBoardSettingsModal}
        group={null}
        board={selectedBoard}
        setBoard={setSelectedBoard}
        onBoardRemoved={() => setPageStore({ navigate, pageName: 'boards' })}
      />
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CrmKanban;
